import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { flow } from 'lodash/fp'
import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'store'

const { actions, reducer } = createSlice({
  name: 'emailPhonePanel',
  initialState: {} as IEmailPhonePanelState,
  reducers: {
    open: () => ({
      isOpen: true
    }),
    close: () => ({
      isOpen: false,
      error: undefined
    }),
    failure: (state, action: PayloadAction<Error>) => ({
      ...state,
      error: action.payload
    }),
    setType: (
      state,
      action: PayloadAction<'Email' | 'Phone Call' | undefined>
    ) => ({
      ...state,
      panelType: action.payload
    })
  }
})

export { reducer as emailPhonePanelReducer }

const rootSelector = (state: AppState) =>
  state.modules.advisory.modules.rdot360.secondaryHeader.interactions
    .emailPhonePanel

const getIsPanelOpen = flow(rootSelector, (x) => x.isOpen)
const getPanelError = flow(rootSelector, (x) => x.error)
const getPanelType = flow(rootSelector, (x) => x.panelType)

export interface IEmailPhonePanelState {
  isOpen?: boolean
  error?: Error
  panelType?: 'Email' | 'Phone Call'
}

export const useEmailPhonePanel = () => {
  const dispatch = useDispatch()

  const close = useCallback(() => {
    dispatch(actions.close())
    dispatch(actions.setType(undefined))
  }, [dispatch])

  const open = useCallback(
    (type?: 'Email' | 'Phone Call') => {
      dispatch(actions.open())
      if (type) {
        dispatch(actions.setType(type))
      }
    },
    [dispatch]
  )

  const setError = useCallback(
    (e: Error) => {
      dispatch(actions.failure(e))
    },
    [dispatch]
  )

  const setPanelType = useCallback(
    (type?: 'Email' | 'Phone Call') => {
      dispatch(actions.setType(type))
    },
    [dispatch]
  )

  const isOpen = useSelector(getIsPanelOpen)

  const error = useSelector(getPanelError)

  const panelType = useSelector(getPanelType)

  return {
    close,
    open,
    setError,
    setPanelType,
    isOpen,
    error,
    panelType
  }
}
